'use client';
import React, { useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, CheckCircle, Clock, Eye, Download, ChevronDown, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const revenueData = [
  { month: 'Feb', deposits: 210000, withdrawals: 145000, commission: 21000, net: 65000 },
  { month: 'Mar', deposits: 245000, withdrawals: 162000, commission: 24500, net: 83000 },
  { month: 'Apr', deposits: 198000, withdrawals: 130000, commission: 19800, net: 68000 },
  { month: 'May', deposits: 280000, withdrawals: 190000, commission: 28000, net: 90000 },
  { month: 'Jun', deposits: 305000, withdrawals: 210000, commission: 30500, net: 95000 },
  { month: 'Jul', deposits: 312450, withdrawals: 215000, commission: 31245, net: 97450 },
];

const commissionData = [
  { name: 'Quiz', value: 38, color: '#7C3AED' },
  { name: 'Challenges', value: 28, color: '#06B6D4' },
  { name: 'Games', value: 18, color: '#10B981' },
  { name: 'Tournaments', value: 12, color: '#F59E0B' },
  { name: 'Other', value: 4, color: '#64748B' },
];

const transactions = [
  { id: 'TXN-98421', user: 'Arjun Kumar', type: 'deposit', amount: '+$500.00', currency: 'USD', status: 'completed', method: 'Stripe', time: '2m ago', fee: '$1.50' },
  { id: 'TXN-98420', user: 'Sarah Mitchell', type: 'withdrawal', amount: '-$250.00', currency: 'USD', status: 'pending', method: 'Bank Transfer', time: '15m ago', fee: '$2.00' },
  { id: 'TXN-98419', user: 'Carlos Mendez', type: 'commission', amount: '+$45.00', currency: 'USD', status: 'completed', method: 'Platform', time: '32m ago', fee: '$0' },
  { id: 'TXN-98418', user: 'Priya Sharma', type: 'deposit', amount: '+$100.00', currency: 'INR', status: 'completed', method: 'Razorpay', time: '1h ago', fee: '₹5' },
  { id: 'TXN-98417', user: 'James Wilson', type: 'withdrawal', amount: '-$4,500.00', currency: 'USD', status: 'review', method: 'Bank Transfer', time: '2h ago', fee: '$10.00' },
  { id: 'TXN-98416', user: 'Emma Thompson', type: 'prize', amount: '+$800.00', currency: 'USD', status: 'completed', method: 'Wallet', time: '3h ago', fee: '$0' },
  { id: 'TXN-98415', user: 'Liu Wei', type: 'refund', amount: '+$50.00', currency: 'USD', status: 'completed', method: 'Stripe', time: '5h ago', fee: '$0' },
];

const typeConfig: Record<string, { color: string; bg: string }> = {
  deposit: { color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  withdrawal: { color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
  commission: { color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  prize: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  refund: { color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' },
};

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  completed: { color: '#10B981', icon: CheckCircle },
  pending: { color: '#F59E0B', icon: Clock },
  review: { color: '#F43F5E', icon: Eye },
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg p-3 text-xs" style={{ background: '#1E293B', border: '1px solid #1E2A4A' }}>
        <p className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>{label}</p>
        {payload.map((p: any) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: ${(p.value / 1000).toFixed(1)}k</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AdminFinanceContent() {
  const [txFilter, setTxFilter] = useState('all');

  const filtered = transactions.filter(t => txFilter === 'all' || t.type === txFilter);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Finance Tracking</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Deposits, withdrawals, commission and revenue overview</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><Download size={14} /> Export CSV</button>
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Deposits', value: '$312,450', change: '+22.3%', up: true, icon: TrendingUp, color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
          { label: 'Total Withdrawals', value: '$215,000', change: '+15.1%', up: false, icon: TrendingDown, color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
          { label: 'Commission Earned', value: '$31,245', change: '+18.7%', up: true, icon: DollarSign, color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
          { label: 'Net Platform Revenue', value: '$97,450', change: '+24.2%', up: true, icon: TrendingUp, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
        ].map((card) => (
          <div key={card.label} className="rounded-xl p-4 card-hover" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                <card.icon size={18} style={{ color: card.color }} />
              </div>
              <span className={`text-xs font-semibold flex items-center gap-0.5 ${card.up ? 'text-emerald-400' : 'text-rose-400'}`}>
                {card.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{card.change}
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{card.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{card.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">Revenue Trend</h3>
            <span className="text-xs px-2 py-1 rounded-md" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={revenueData}>
              <defs>
                {[{ id: 'dep', color: '#7C3AED' }, { id: 'with', color: '#F43F5E' }, { id: 'net', color: '#10B981' }].map(g => (
                  <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={g.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={g.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2A4A" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v / 1000}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#7C3AED" strokeWidth={2} fill="url(#dep)" />
              <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#F43F5E" strokeWidth={2} fill="url(#with)" />
              <Area type="monotone" dataKey="net" name="Net Revenue" stroke="#10B981" strokeWidth={2} fill="url(#net)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="text-sm font-semibold text-foreground mb-4">Commission by Category</h3>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={commissionData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={0}>
                {commissionData.map((entry, i) => <Cell key={`cell-${i}`} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1E293B', border: '1px solid #1E2A4A', borderRadius: 8, fontSize: 11 }} formatter={(v: any) => [`${v}%`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {commissionData.map((d) => (
              <div key={d.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{d.name}</span>
                </div>
                <span className="text-xs font-semibold text-foreground">{d.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-sm font-semibold text-foreground">Recent Transactions</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <select value={txFilter} onChange={e => setTxFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-1.5 text-xs rounded-lg outline-none cursor-pointer"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="commission">Commission</option>
                <option value="prize">Prizes</option>
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                {['Transaction ID', 'User', 'Type', 'Amount', 'Method', 'Fee', 'Status', 'Time', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((tx) => {
                const tc = typeConfig[tx.type] || { color: '#64748B', bg: 'rgba(100,116,139,0.12)' };
                const sc = statusConfig[tx.status] || { color: '#64748B', icon: Clock };
                return (
                  <tr key={tx.id} className="border-b hover:bg-muted/50 transition-colors" style={{ borderColor: 'var(--border)' }}>
                    <td className="px-4 py-3 text-xs font-mono" style={{ color: '#A78BFA' }}>{tx.id}</td>
                    <td className="px-4 py-3 text-xs text-foreground">{tx.user}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold capitalize"
                        style={{ background: tc.bg, color: tc.color }}>{tx.type}</span>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold" style={{ color: tx.amount.startsWith('+') ? '#10B981' : '#F43F5E' }}>{tx.amount}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{tx.method}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{tx.fee}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs font-semibold capitalize" style={{ color: sc.color }}>
                        <sc.icon size={11} />{tx.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{tx.time}</td>
                    <td className="px-4 py-3">
                      <button className="p-1 rounded hover:bg-muted transition-colors">
                        <Eye size={13} style={{ color: 'var(--muted-foreground)' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
